export const PLAN_FEATURES = {
  NETS_CUSTOMERS: {
    EXECUTIVE_REPORT_ENABLED: "nets_customers.executive_report_enabled",
    FEDERATED_OIDC: "nets_customers.federated_oidc",
  },
  NETS_USER: {
    DOCUMENTS_PREVIEW: "nets_user.documents_preview",
    ADD_USER_DOCUMENT: "nets_user.add_user_document",
    USER_CAN_SHARE_IDENTITY: "nets_user.user_can_share_identity",
    EDIT_USER_ADDRESS: "nets_user.edit_user_address",
  },
  NETS_NOTIFY: {
    SMART_NOTIFICATIONS: "nets_notify.smart_notificactions",
    ASSIGNMENT_HISTORY: "nets_notify.assignment_history",
  },
  NETS_CORPORATE: {
    AI_ANALYSIS: "nets_corporate.ai_analysis",
    ADD_USER_PROCEDURES: "nets_corporate.add_user_procedures",
    ADD_PROCEDURE_EXTRA_TAX: "nets_corporate.add_procedure_extra_tax",
    PROCEDURES_REQUIREMENTS_AI: "nets_corporate.procedures_requirements_ai",
  },
  NETS_REQUESTS: "nets_requests",
  NETS_TAXES: {
    MAPS_INCLUDED: "nets_taxes.maps_included",
    AI_ANALYSIS: "nets_taxes.ai_analysis",
    LICENSE_SUMMARY: "nets_taxes.license_summary",
    LICENSE_BADGE_QR: "nets_taxes.license_badge_qr",
  },
  NETS_URBIN: {
    MAPS_INCLUDED: "nets_urbin.maps_included",
  },
  NETS_ENVIRONMENT: {
    MAPS_INCLUDED: "nets_environment.maps_included",
  },
  NETS_FIRE_DEPARTMENT: {
    ENABLED: "nets_fire_department.enabled",
    MAPS_INCLUDED: "nets_fire_department.maps_included",
    OPERATIONS_ENABLED: "nets_fire_department.operations_enabled",
  },
  NETS_CADASTRE: {
    REAL_ESTATE_INSCRIPTIONS: "nets_cadastre.real_estate_inscriptions",
    MAPS_ENABLED: "nets_cadastre.maps_enabled",
    AI_SKETCH: "nets_cadastre.ai_sketch",
    EXTRA_STATEMENT_CHARGES: "nets_cadastre.extra_statement_charges",
  },
  NETS_MOTOR: {
    AI_ANALYSIS: "nets_motor.ai_analysis",
    CAN_ADD_USER_VEHICLE: "nets_motor.can_add_user_vehicle",
    MAPS_INCLUDED: "nets_motor.maps_included",
    PASSENGER_SALES: "nets_motor.passenger_sales",
    PASSENGER_DIGITAL_FILL: "nets_motor.passenger_digital_fill",
    PASSENGER_OFFLINE_IMPORT: "nets_motor.passenger_offline_import",
  },
  NETS_EVENTADS: {
    MAPS_INCLUDED: "nets_eventads.maps_included",
    PUBLIC_EVENTS: "nets_eventads.public_events",
    EVENT_CATEGORIES: "nets_eventads.event_categories",
    EVENT_STATES: "nets_eventads.event_states",
    EVENT_TICKETING: "nets_eventads.event_ticketing",
    EVENT_ONLINE_SALES: "nets_eventads.event_online_sales",
    EVENT_STRIPE_PAYMENTS: "nets_eventads.event_stripe_payments",
    EVENT_MOBILE_PAYMENTS: "nets_eventads.event_mobile_payments",
    EVENT_TRANSFER_PAYMENTS: "nets_eventads.event_transfer_payments",
    EVENT_CALENDAR_IMPORT: "nets_eventads.event_calendar_import",
    EVENT_QR_VALIDATION: "nets_eventads.event_qr_validation",
  },
  NETS_AI: {
    GUEST_GEOLOCATION: "nets_ai.guest_geolocation",
  },
  NETS_DOCUMENTS: {
    ENABLED: "nets_documents.enabled",
  },
  NETS_SERVICE: {
    ROUTE_TRACKING: "nets_service.route_tracking",
    RETENTION_MANAGEMENT_V2: "nets_service.retention_management_v2",
    CONDOMINIUM_GROUPS: "nets_service.condominium_groups.enabled",
    CONDOMINIUM_BILLING: "nets_service.condominium_billing.enabled",
  },
  CLASSROOM: {
    ENABLED: "classroom.enabled",
    CE_COMPLIANCE: "classroom.ce_compliance",
    ONLINE_SALES: "classroom.online_sales",
    STRIPE_PAYMENTS: "classroom.stripe_payments",
    CERTIFICATES: "classroom.certificates",
    PROCTORING: "classroom.proctoring",
    LICENSE_VERIFICATION: "classroom.license_verification",
    EXAM_SIMULATOR: "classroom.exam_simulator",
    EXAM_BANK_IMPORT: "classroom.exam_bank_import",
  },
};

const FEATURES_CATALOG_URL = "/djstatic/features.json";
const FEATURES_CATALOG_CACHE_KEY = "arkadu.features.catalog.v1";
const FEATURES_CATALOG_LAST_CHECK_KEY = "arkadu.features.catalog.lastCheck.v1";
const FEATURES_CATALOG_CHECK_INTERVAL_MS = 6 * 60 * 60 * 1000;

const hasWindowStorage = () =>
  typeof window !== "undefined" && typeof window.localStorage !== "undefined";

const readLocalCatalog = () => {
  if (!hasWindowStorage()) {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(FEATURES_CATALOG_CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
};

const writeLocalCatalog = (payload) => {
  if (!hasWindowStorage()) {
    return;
  }

  try {
    window.localStorage.setItem(
      FEATURES_CATALOG_CACHE_KEY,
      JSON.stringify(payload),
    );
  } catch (error) {
    // ignore localStorage failures to avoid blocking app startup
  }
};

const readLastCheck = () => {
  if (!hasWindowStorage()) {
    return 0;
  }

  const raw = window.localStorage.getItem(FEATURES_CATALOG_LAST_CHECK_KEY);
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : 0;
};

const writeLastCheck = (timestamp = Date.now()) => {
  if (!hasWindowStorage()) {
    return;
  }

  try {
    window.localStorage.setItem(
      FEATURES_CATALOG_LAST_CHECK_KEY,
      String(timestamp),
    );
  } catch (error) {
    // ignore localStorage failures to avoid blocking app startup
  }
};

const fetchRemoteCatalog = async () => {
  const response = await fetch(FEATURES_CATALOG_URL, {
    method: "GET",
    headers: { Accept: "application/json" },
  });

  if (!response.ok) {
    throw new Error(`Unable to load features catalog: ${response.status}`);
  }

  return response.json();
};

export const syncPlanFeaturesCatalog = async () => {
  const cached = readLocalCatalog();
  const remote = await fetchRemoteCatalog();
  const remoteVersion = remote?.version || null;
  const sameVersion = Boolean(
    remoteVersion && remoteVersion === cached?.version,
  );

  writeLastCheck();
  if (!sameVersion && remoteVersion) {
    writeLocalCatalog(remote);
    return remote;
  }

  return cached || remote;
};

export const loadPlanFeaturesCatalog = async ({
  forceRefresh = false,
} = {}) => {
  const cached = readLocalCatalog();
  const hasCached = Boolean(cached?.version && cached?.features);
  const shouldCheckRemote =
    forceRefresh ||
    !hasCached ||
    Date.now() - readLastCheck() > FEATURES_CATALOG_CHECK_INTERVAL_MS;

  if (hasCached && !shouldCheckRemote) {
    return cached;
  }

  try {
    return await syncPlanFeaturesCatalog();
  } catch (error) {
    if (hasCached) {
      return cached;
    }
    throw error;
  }
};

export const preloadPlanFeaturesCatalog = () => {
  loadPlanFeaturesCatalog().catch(() => null);
};

export default PLAN_FEATURES;
